/* Shared table primitives for admin pages */

import { t } from "../i18n";

export function Th({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return <th style={styles.th}>{children}</th>;
}

export function Td({
  children,
  colSpan,
  mono,
}: {
  children: React.ReactNode;
  colSpan?: number;
  mono?: boolean;
}): React.JSX.Element {
  const style = mono
    ? { ...styles.td, fontFamily: "var(--font-mono)" }
    : styles.td;
  return (
    <td style={style} colSpan={colSpan}>
      {children}
    </td>
  );
}

export function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}): React.JSX.Element {
  if (totalPages <= 1) return <></>;
  return (
    <div style={styles.pagination}>
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        style={styles.pageBtn}
      >
        {t("pagination.prev")}
      </button>
      <span style={styles.pageInfo}>
        {page} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        style={styles.pageBtn}
      >
        {t("pagination.next")}
      </button>
    </div>
  );
}

/* ---------- Helpers ---------- */

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}...` : id;
}

/* ---------- Re-usable table styles ---------- */

export const tableStyles: Record<string, React.CSSProperties> = {
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: 13,
  },
  th: {
    textAlign: "left" as const,
    padding: "8px 12px",
    fontWeight: 600,
    fontSize: 12,
    color: "var(--color-text-secondary)",
    borderBottom: "1px solid var(--color-border)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.03em",
  },
  td: {
    padding: "8px 12px",
    borderBottom: "1px solid var(--color-border)",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    justifyContent: "center",
  },
  pageBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  pageInfo: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
  },
  error: {
    color: "var(--color-danger)",
    fontSize: 13,
    marginBottom: 12,
  },
  loadingText: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    marginTop: 8,
  },
  filterRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap" as const,
    marginBottom: 16,
  },
  filterInput: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    width: 150,
  },
  filterBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
};

const styles = tableStyles;
